import { useCallback, useEffect, useRef, useState } from 'react'
import { getRemoteValue, saveRemote } from './remotePreferences'

const STORAGE_KEY = 'chart_layout_state'
const CONFIG_VERSION = 2

const DEFAULTS = Object.freeze({
  chartType: 'candles',
  palette: 'classic',
  chartBg: 'default',
  drawingToolbarVisible: false,
  timeDisplay: 'local',
  _version: CONFIG_VERSION,
})

function migrateConfig(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { ...DEFAULTS }
  const cfg = { ...DEFAULTS, ...parsed }
  const v = cfg._version ?? 1
  if (v < 2) {
    // v1 → v2: añade preferencia de zona horaria del eje (default local).
    if (!('timeDisplay' in parsed)) cfg.timeDisplay = 'local'
  }
  cfg._version = CONFIG_VERSION
  return cfg
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw)
    return migrateConfig(parsed)
  } catch {
    return { ...DEFAULTS }
  }
}

function save(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // ignore quota/private-mode errors
  }
}

function reconcile(prev, remote) {
  if (!remote || typeof remote !== 'object' || Array.isArray(remote)) return prev
  const merged = { ...prev, ...remote }
  const v = remote._version ?? 1
  if (v < 2 && !('timeDisplay' in merged)) {
    merged.timeDisplay = 'local'
  }
  merged._version = CONFIG_VERSION
  return merged
}

export function useChartLayout() {
  const [layout, setLayout] = useState(load)
  const remoteAppliedRef = useRef(false)

  // Al montar, si el backend ya tenía un layout guardado para esta cuenta
  // (otro dispositivo, u otro usuario en este mismo navegador), lo aplica
  // por encima del que se leyó de localStorage. Si el backend no tiene nada
  // (cuenta nueva, o remoto no configurado en esta página), no toca nada.
  // La migración corre en AMBOS caminos de carga (localStorage y remoto).
  useEffect(() => {
    let cancelled = false
    getRemoteValue(STORAGE_KEY).then((remote) => {
      if (cancelled || remoteAppliedRef.current || !remote) return
      remoteAppliedRef.current = true
      setLayout(prev => {
        const next = reconcile(prev, remote)
        save(next)
        return next
      })
    })
    return () => { cancelled = true }
  }, [])

  const update = useCallback((key, value) => {
    setLayout(prev => {
      const next = { ...prev, [key]: value }
      save(next)
      saveRemote(STORAGE_KEY, next)
      return next
    })
  }, [])

  const updateAll = useCallback((patch) => {
    setLayout(prev => {
      const next = { ...prev, ...patch }
      save(next)
      saveRemote(STORAGE_KEY, next)
      return next
    })
  }, [])

  return { layout, update, updateAll }
}
